import { NextFunction, Request, Response } from "express"
import pm from "picomatch"
import proxyAddr, { all, compile } from "proxy-addr"
import { TailscaleLocalApi, WhoIsResponse as TailscaleWhoisResponse } from "tailscale-local-api"
import { tailscaleCapabilityMethods, TailscaleCapabilitySchema, tailscaleCapabilitySchema } from "./tailscale/schema.js"
import { checkTrustProxyConfiguration } from "./utils/checkTrustProxyConfig.js"


declare module "express-serve-static-core" {
  interface Request {
    tailscaleUser?: TailscaleWhoisResponse["userProfile"] & { capabilities: TailscaleCapabilitySchema }
  }
}
export type RequestWithTailscaleUser = Request & {
  tailscaleUser?: TailscaleWhoisResponse["userProfile"] & { capabilities: TailscaleCapabilitySchema }
}

let trustProxyWarningShown = false


export interface TailscaleAuthMiddlewareOptions {
  /**
   * The path to the tailscaled unix socket. 
   * By default will look for common location on specific platforms.
   * 
   * Unless you're running tailscale in a special way, you probably don't need to set this.
   * @example '/var/run/tailscale/tailscaled.sock'
   */
  socketPath?: string
  
  /**
   * If true, will only use the unix socket to connect to tailscaled.
   * If false, will use the localhost TCP port to connect to tailscaled.
   * 
   * Unless you're running tailscale in a special way, you probably don't need to set this.
   * 
   * @default false
   */
  useSocketOnly?: boolean
  
  /**
   * Whether to enable debug mode.
   * If enabled, the middleware will log debug information to the console.
   * 
   * @default false
   */
  debug?: boolean
  
  /**
   * The capabilities namespace to use for the Tailscale Grants.
   * If not set, the middleware will assume the user has access to every method on every route.
   * 
   * @example
   * Set capabilitiesNamespace as "test.com/cap/express" for grants:
   * ```json
   * "grants": [{
   *   "src": ["user@example.com"],
   *   "dst": ["*"],
   *   "app": {
   *     "test.com/cap/express": [
   *       {
   *         "routes": [
   *           {"route": "/api", "methods": ["*"]},
   *           {"route": "/api/**", "methods": ["GET", "POST"]}
   *         ]
   *       }
   *     ]
   *   }
   * }]
   * ```
   */
  capabilitiesNamespace?: string
}

export const createTailscaleAuthMw = (
  /**
   * Configuration options for the Tailscale authentication middleware.
   * 
   * @example
   * ```typescript
   * const authMiddleware = createTailscaleAuthMw({
   *   socketPath: '/var/run/tailscale/tailscaled.sock',
   *   useSocketOnly: true,
   *   debug: true,
   *   capabilitiesNamespace: 'test.com/cap/express'
   * })
   * ```
   * 
   * @example
   * Set capabilitiesNamespace as "test.com/cap/express" for grants:
   * 
   * ```json
   * "grants": [{
   *   "src": ["user@example.com"],
   *   "dst": ["*"],
   *   "app": {
   *     "test.com/cap/express": [
   *       {
   *         "routes": [
   *           {"route": "/api", "methods": ["*"]},
   *           {"route": "/api/**", "methods": ["GET", "POST"]}
   *         ]
   *       }
   *     ]
   *   }
   * }]
   * ```
   */
  options: TailscaleAuthMiddlewareOptions = {}
) => {
  
  const localApi = new TailscaleLocalApi(options)
  const debug = options.debug || false

  

  const getIp = (req: Request) => {
    if (debug) console.debug("all addrs of req", all(req))

    if(!trustProxyWarningShown && checkTrustProxyConfiguration(req)){
      trustProxyWarningShown = true
    }

    const trustProxy = req.app.get('trust proxy')
    
    if (trustProxy === false || trustProxy === undefined) {
      if (debug) console.debug("Not trusting reverse proxy, returning socket address", req.socket.remoteAddress)
      return req.socket.remoteAddress
    }
    
    if (typeof trustProxy === "boolean" && trustProxy === true) {
      return all(req).at(-1)
    }


    const trust = compile(trustProxy)
    const finalAddress = proxyAddr(req, trust)
    if (debug) console.debug("trusting remote address", finalAddress)
    return finalAddress
  }

  return async (req: Request, res: Response, next: NextFunction) => {
    const rawClientIp = getIp(req)
    if (!rawClientIp) {
      if (debug) console.debug("No client IP found", rawClientIp)
      res.status(404).send(`Cannot ${req.method} ${req.path}`)
      return
    }
    const gatedClientIp = localApi.isInTailscaleIpRange(rawClientIp)
    if (!gatedClientIp) {
      if (debug) console.debug("Request IP not from Tailscale range", rawClientIp)
      res.status(404).send(`Cannot ${req.method} ${req.path}`)
      return
    }
    let parsedCapabilities
    try {
      const whois = await localApi.whoIs(gatedClientIp)

      // If a capabilities namespace is set, read the capabilities from the Tailscale ACL
      // If not, assume the user has access to every method on every route
      const capabilities = options.capabilitiesNamespace
        ? whois.capMap[options.capabilitiesNamespace]?.[0]
        : { routes: [{ route: "**", methods: ["*"] }] }

      const parseResult = tailscaleCapabilitySchema.safeParse(capabilities)
      parsedCapabilities = parseResult.data
      req.tailscaleUser = {...whois.userProfile, capabilities: parsedCapabilities ?? {}}

      if (!parseResult.success) {
        if (debug) console.debug("Couldn't find or parse capabilities", parseResult.error)
        res.status(401).send(`Cannot ${req.method} ${req.path}`)
        return
      }
    } catch (e) {
      console.error(e)
      res.status(401).send(`Cannot ${req.method} ${req.path}`)
      return
    }

    const route = req.originalUrl
    const parsed = tailscaleCapabilityMethods.safeParse(req.method)
    if (!parsed.success) {
      if (debug) console.debug("Invalid method", parsed.error)
      res.status(401).send(`Cannot ${req.method} ${req.path}`)
      return
    }
    
    const method = parsed.data
    const hasCapability = parsedCapabilities?.routes?.some((cap) => {
      const isMatch = pm(cap.route)
      const routeMatch = isMatch(route)
      if (!routeMatch) return false
      return cap.methods.includes("*") || cap.methods.includes(method)
    })
    if (!hasCapability) {
      if (debug) console.debug("Requester has no capability for route", req.path, req.method)
      res.status(401).send(`Cannot ${req.method} ${req.path}`)
      return
    }
    next()
  }
}
